/**
 * WebsiteOrdersList — live website orders for the current lead.
 *
 * Two-stage UX:
 *  1. Auto-fetched on chat open from exoticindia.com (admin_crm.php).
 *  2. Read-only summary card per order. Tap to expand → items, ship/bill
 *     addresses, txn id, and per-item Cancel buttons (Iter 151).
 *
 * Sits inside the chat right-panel "order" tab. Extracted from
 * `WebsiteOrdersPanel.jsx` during the Feb 2026 code-quality split.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Loader2, Package, RefreshCw, ShoppingBag, AlertCircle, Sparkles,
  ChevronUp, ChevronDown,
} from "lucide-react";
import { websiteOrdersApi, booksApi } from "../../lib/api";
import { StatusBadge, formatMoney } from "./websiteOrdersUtils";
import { CancelItemButton } from "./CancelItemButton";
import { CancelAttemptsBadge } from "./CancelAttemptsBadge";

export const WebsiteOrdersList = ({ leadId, lead }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ looked_up_by: null, error: null });
  const [expanded, setExpanded] = useState({});
  // Yash, Apr 29 2026 — productImages caches catalog thumbnails keyed by
  // itemcode. Used as a fallback when the order's `order_image` field is
  // missing/broken. Lazy-fetched on order expand so we don't fire 50
  // /api/books/* requests for collapsed orders.
  //   value: { url: string|null, loading: bool }
  const [productImages, setProductImages] = useState({});
  // Section-level collapse so agents can hide the whole list when they
  // don't need it. Default open so the panel is discoverable.
  const [sectionOpen, setSectionOpen] = useState(true);

  const refresh = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await websiteOrdersApi.byLead(leadId);
      setOrders(res.data?.orders || []);
      setMeta({
        looked_up_by: res.data?.looked_up_by,
        looked_up_value: res.data?.looked_up_value,
        error: res.data?.error || res.data?.reason || null,
      });
    } catch (e) {
      setMeta({ error: e.response?.data?.detail || "Failed to fetch website orders" });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Lazy-fetch catalog thumbnails for the items in a single order. Only
  // fires for itemcodes we haven't already attempted (success or fail).
  const fetchProductImagesForOrder = useCallback(async (order) => {
    const codes = (order.items || [])
      .map((it) => (it.itemcode || "").trim().toUpperCase())
      .filter(Boolean);
    const fresh = codes.filter((c) => productImages[c] === undefined);
    if (fresh.length === 0) return;
    // Mark as loading immediately so concurrent expands don't re-fire.
    setProductImages((m) => {
      const next = { ...m };
      fresh.forEach((c) => { next[c] = { url: null, loading: true }; });
      return next;
    });
    await Promise.all(
      fresh.map(async (code) => {
        try {
          const res = await booksApi.lookup(code);
          const det = res.data?.raw?.details || {};
          const url = det.image || res.data?.raw?.image || null;
          setProductImages((m) => ({ ...m, [code]: { url, loading: false } }));
        } catch {
          // 404 / network — just remember we tried so we don't loop.
          setProductImages((m) => ({ ...m, [code]: { url: null, loading: false } }));
        }
      })
    );
  }, [productImages]);

  // ── AI repeat-buyer one-liner ──────────────────────────────────────────
  // Yash, Apr 28 2026. Cheap inline summary — no LLM call, just deterministic
  // pattern over the fetched orders. Gives agents an instant cue about a
  // customer's value before they craft their reply.
  const summary = useMemo(() => {
    if (!orders.length) return null;
    const totalsByCur = {};
    const cats = {};
    let earliest = null;
    let latest = null;
    let delivered = 0;
    let pending = 0;
    for (const o of orders) {
      const cur = (o.fx_type || "INR").toUpperCase();
      totalsByCur[cur] = (totalsByCur[cur] || 0) + (Number(o.total) || 0);
      const ts = (o.ordered_time || "").trim();
      if (ts && !ts.startsWith("1970")) {
        if (!earliest || ts < earliest) earliest = ts;
        if (!latest || ts > latest) latest = ts;
      }
      const st = String(o.order_status || "").toLowerCase();
      if (st.includes("deliver")) delivered++;
      else if (st && !st.includes("cancel")) pending++;
      // Tag categories from item titles (cheap heuristic)
      for (const it of o.items || []) {
        const t = String(it.order_title || "").toLowerCase();
        if (/book|published|publish/.test(t)) cats.book = (cats.book || 0) + 1;
        else if (/painting|tanjore|miniature/.test(t)) cats.painting = (cats.painting || 0) + 1;
        else if (/sculpture|brass|bronze|stone|wood/.test(t)) cats.sculpture = (cats.sculpture || 0) + 1;
        else if (/jewel|necklace|earring|pendant/.test(t)) cats.jewelry = (cats.jewelry || 0) + 1;
        else if (/saree|silk|fabric/.test(t)) cats.fabric = (cats.fabric || 0) + 1;
      }
    }
    const dominantCat = Object.entries(cats).sort((a, b) => b[1] - a[1])[0]?.[0];
    const totalsStr = Object.entries(totalsByCur)
      .map(([cur, amt]) => `${cur === "USD" ? "$" : cur === "EUR" ? "€" : cur === "GBP" ? "£" : "₹"}${Math.round(amt).toLocaleString("en-IN")}`)
      .join(" + ");
    const repeatLabel = orders.length >= 5 ? "loyal repeat buyer" : orders.length >= 2 ? "repeat buyer" : "first-time buyer";
    const tier = (() => {
      const inrEquiv = (totalsByCur.INR || 0) + (totalsByCur.USD || 0) * 84 + (totalsByCur.EUR || 0) * 90 + (totalsByCur.GBP || 0) * 105;
      if (inrEquiv >= 50000) return "VIP";
      if (inrEquiv >= 20000) return "high-value";
      return null;
    })();
    return {
      orderCount: orders.length,
      totalsStr,
      dominantCat,
      delivered,
      pending,
      repeatLabel,
      tier,
    };
  }, [orders]);

  return (
    <div className="p-4 border-b border-slate-100" data-testid="website-orders-section">
      <div className="flex items-center justify-between mb-2.5">
        <button
          onClick={() => setSectionOpen((o) => !o)}
          className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
          data-testid="website-orders-toggle"
          title={sectionOpen ? "Collapse website orders" : "Expand website orders"}
        >
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-sm">
            <ShoppingBag size={13} />
          </div>
          <div>
            <p className="text-[12px] font-extrabold text-[--text-primary] leading-tight flex items-center gap-1.5">
              Website Orders (Live)
              {orders.length > 0 && (
                <span className="text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">{orders.length}</span>
              )}
              {sectionOpen ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
            </p>
            <p className="text-[9px] text-[--text-muted] leading-tight">
              {meta.looked_up_by ? `via ${meta.looked_up_by}: ${meta.looked_up_value || ""}` : "from exoticindia.com"}
            </p>
          </div>
        </button>
        {sectionOpen && (
          <button
            onClick={refresh}
            disabled={loading}
            className="text-[10px] text-amber-600 hover:text-amber-800 font-bold flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-amber-50 transition-all disabled:opacity-40"
            data-testid="website-orders-refresh-btn"
          >
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
          </button>
        )}
      </div>

      {!sectionOpen ? null : (<>

      {/* AI summary banner — appears as soon as we have ≥1 order */}
      {summary && (
        <div className="mb-2.5 rounded-xl bg-gradient-to-r from-violet-50 via-fuchsia-50 to-pink-50 border border-violet-200 px-3 py-2 flex items-start gap-2" data-testid="repeat-buyer-summary">
          <Sparkles size={12} className="text-violet-600 mt-0.5 shrink-0" />
          <p className="text-[10.5px] text-violet-900 leading-snug">
            <span className="font-extrabold">{summary.repeatLabel.replace(/^./, c => c.toUpperCase())}</span>
            {" — "}{summary.orderCount} order{summary.orderCount === 1 ? "" : "s"} totalling <span className="font-bold">{summary.totalsStr}</span>
            {summary.dominantCat && <> · mostly <span className="font-semibold">{summary.dominantCat}s</span></>}
            {summary.delivered > 0 && <> · {summary.delivered} delivered</>}
            {summary.pending > 0 && <> · {summary.pending} pending</>}
            {summary.tier && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold uppercase tracking-wider bg-yellow-100 text-yellow-800 border border-yellow-300 align-middle">
                {summary.tier}
              </span>
            )}
          </p>
        </div>
      )}

      {loading && orders.length === 0 && (
        <div className="text-[11px] text-slate-400 italic flex items-center gap-1.5 py-3">
          <Loader2 size={11} className="animate-spin" /> Pulling from exoticindia.com…
        </div>
      )}

      {!loading && meta.error && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 text-[10px] text-amber-800 flex items-start gap-1.5" data-testid="website-orders-error">
          <AlertCircle size={11} className="mt-0.5 shrink-0" />
          <span><span className="font-bold">Couldn't fetch:</span> {meta.error}</span>
        </div>
      )}

      {!loading && !meta.error && orders.length === 0 && (
        <p className="text-[11px] text-slate-400 italic py-2" data-testid="website-orders-empty">
          No website orders found for this customer.
        </p>
      )}

      <div className="space-y-2" data-testid="website-orders-list">
        {orders.map((o) => {
          const isOpen = !!expanded[o.id];
          return (
            <div key={o.id} className="bg-white border border-slate-200 rounded-xl p-2.5 shadow-sm" data-testid={`website-order-${o.id}`}>
              <button
                onClick={() => {
                  const willOpen = !isOpen;
                  setExpanded((s) => ({ ...s, [o.id]: willOpen }));
                  if (willOpen) fetchProductImagesForOrder(o);
                }}
                className="w-full flex items-start justify-between gap-2 text-left"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[12px] font-extrabold text-slate-900 font-mono">#{o.id}</span>
                    <StatusBadge status={o.order_status} />
                    {o.payment_type && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200">
                        {o.payment_type}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {o.first_name} {o.last_name} · {o.shipping_city || o.city} · {o.items?.length || 0} item(s)
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[12px] font-extrabold text-amber-700" style={{ fontFamily: "Outfit" }}>
                    {formatMoney(o.total, o.fx_type, o.fx_notation)}
                  </p>
                </div>
              </button>

              {isOpen && (
                <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
                  {/* Iter 156.34 — temporary cancel-upstream debug indicator.
                      Shows status of last upstream cancel attempt for this
                      order so CS can spot silent-failure cases inline. */}
                  <CancelAttemptsBadge orderId={o.id} />
                  {o.items?.map((it, i) => {
                    // Fallback image: if the upstream `order_image` is empty
                    // or fails to render, use the catalog image fetched via
                    // /api/books/{itemcode}. Both render at the same size so
                    // the layout doesn't shift when the fallback resolves.
                    const code = (it.itemcode || "").trim().toUpperCase();
                    const cached = productImages[code];
                    const upstreamImg = it.order_image
                      ? `https://www.exoticindia.com/images/${it.order_image}`
                      : null;
                    const fallbackImg = cached?.url || null;
                    const imgSrc = upstreamImg || fallbackImg;
                    return (
                      <div key={`${o.id}-it-${i}`} className="flex items-start gap-2 bg-slate-50 rounded-lg p-2" data-testid={`website-order-item-${o.id}-${i}`}>
                        {imgSrc ? (
                          <a
                            href={imgSrc}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded overflow-hidden bg-white border border-slate-200 flex-shrink-0 hover:ring-2 hover:ring-emerald-300 transition-all"
                          >
                            <img
                              src={imgSrc}
                              alt={it.order_title || code}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                // Upstream broken & no fallback yet → swap to fallback if it
                                // resolves later, otherwise mark this img hidden.
                                if (fallbackImg && e.currentTarget.src !== fallbackImg) {
                                  e.currentTarget.src = fallbackImg;
                                } else {
                                  e.currentTarget.parentElement.style.display = "none";
                                }
                              }}
                              data-testid={`website-order-item-img-${o.id}-${i}`}
                            />
                          </a>
                        ) : cached?.loading ? (
                          <div className="w-10 h-10 rounded bg-slate-100 border border-slate-200 flex-shrink-0 flex items-center justify-center">
                            <Loader2 size={11} className="animate-spin text-slate-400" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded bg-slate-100 border border-slate-200 flex-shrink-0 flex items-center justify-center text-slate-400">
                            <Package size={12} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[10.5px] font-semibold text-slate-800 line-clamp-2">{it.order_title || it.itemcode}</p>
                          <p className="text-[9px] text-slate-500 font-mono mt-0.5">
                            {it.itemcode}{it.size ? ` · ${it.size}` : ""}{it.color ? ` · ${it.color}` : ""} · qty {Math.round(Number(it.qty) || 0)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <p className="text-[10px] font-bold text-slate-700">{formatMoney(it.order_finalprice, o.fx_type, o.fx_notation)}</p>
                          {/* Iter 151 — Cancel button per unshipped, eligible item (India only, ≤ ₹1500). */}
                          <CancelItemButton order={o} item={it} onCancelled={refresh} />
                        </div>
                      </div>
                    );
                  })}
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div>
                      <p className="text-slate-400 uppercase tracking-wider font-bold">Bill to</p>
                      <p className="text-slate-700 leading-tight mt-0.5">
                        {o.first_name} {o.last_name}<br />
                        {o.address_line1}<br />
                        {o.city}, {o.state} {o.zipcode}<br />
                        {o.country} · {o.mobile}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase tracking-wider font-bold">Ship to</p>
                      <p className="text-slate-700 leading-tight mt-0.5">
                        {o.shipping_first_name || o.first_name} {o.shipping_last_name || o.last_name}<br />
                        {o.shipping_address_line1 || o.address_line1}<br />
                        {(o.shipping_city || o.city)}, {(o.shipping_state || o.state)} {(o.shipping_zipcode || o.zipcode)}<br />
                        {o.shipping_country || o.country}
                      </p>
                    </div>
                  </div>
                  {o.transid && (
                    <p className="text-[9px] text-slate-500 font-mono">Txn: {o.transid}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>)}
    </div>
  );
};
